// Toolbar for the BriefEditor.
// Insert Reference menu: Footnote, Endnote, Bibliography (multi-select picker).
import type { Editor } from "@tiptap/react";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Quote,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Plus,
  Hash,
  BookMarked,
  Library,
  Quote as QuoteIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  IndentIncrease,
  IndentDecrease,
  FileText,
  XCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type ApprovedBriefToolbarControl = {
  /** True when the piece currently has an approved brief attached. */
  active: boolean;
  /** Clear the approved brief so the next Writing Agent run has no brief handoff. */
  onClear: () => void | Promise<void>;
  /** True while the clear request is in-flight. */
  clearing?: boolean;
};

type Props = {
  editor: Editor;
  onInsertFootnote: () => void;
  onInsertEndnote: () => void;
  onInsertBibliography: () => void;
  onInsertCitation: () => void;
  /** Optional Approved-Brief control rendered after "Insert Reference". */
  approvedBrief?: ApprovedBriefToolbarControl;
};

const tBtn = "h-8 w-8 p-0";

export const BriefEditorToolbar = ({
  editor,
  onInsertFootnote,
  onInsertEndnote,
  onInsertBibliography,
  onInsertCitation,
  approvedBrief,
}: Props) => {
  const promptLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL", prev ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border-b bg-muted/40 px-2 py-1.5">
      <Button type="button" variant={editor.isActive("bold") ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().toggleBold().run()} aria-label="Bold"><Bold className="h-4 w-4" /></Button>
      <Button type="button" variant={editor.isActive("italic") ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().toggleItalic().run()} aria-label="Italic"><Italic className="h-4 w-4" /></Button>
      <Button type="button" variant={editor.isActive("underline") ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().toggleUnderline().run()} aria-label="Underline"><UnderlineIcon className="h-4 w-4" /></Button>
      <span className="mx-1 h-5 w-px bg-border" />
      <Button type="button" variant={editor.isActive("heading", { level: 2 }) ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} aria-label="Heading 2"><Heading2 className="h-4 w-4" /></Button>
      <Button type="button" variant={editor.isActive("heading", { level: 3 }) ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} aria-label="Heading 3"><Heading3 className="h-4 w-4" /></Button>
      <Button type="button" variant={editor.isActive("bulletList") ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().toggleBulletList().run()} aria-label="Bullet list"><List className="h-4 w-4" /></Button>
      <Button type="button" variant={editor.isActive("orderedList") ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().toggleOrderedList().run()} aria-label="Ordered list"><ListOrdered className="h-4 w-4" /></Button>
      <Button type="button" variant={editor.isActive("blockquote") ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().toggleBlockquote().run()} aria-label="Quote"><Quote className="h-4 w-4" /></Button>
      <Button type="button" variant={editor.isActive("link") ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={promptLink} aria-label="Link"><LinkIcon className="h-4 w-4" /></Button>
      <span className="mx-1 h-5 w-px bg-border" />
      <Button type="button" variant={editor.isActive({ textAlign: "left" }) ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().setTextAlign("left").run()} aria-label="Align left"><AlignLeft className="h-4 w-4" /></Button>
      <Button type="button" variant={editor.isActive({ textAlign: "center" }) ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().setTextAlign("center").run()} aria-label="Align center"><AlignCenter className="h-4 w-4" /></Button>
      <Button type="button" variant={editor.isActive({ textAlign: "right" }) ? "secondary" : "ghost"} size="sm" className={tBtn} onClick={() => editor.chain().focus().setTextAlign("right").run()} aria-label="Align right"><AlignRight className="h-4 w-4" /></Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={tBtn}
        onClick={() => {
          const ok = editor.isActive("listItem")
            ? editor.chain().focus().liftListItem("listItem").run()
            : false;
          if (!ok) editor.chain().focus().outdentBlock().run();
        }}
        aria-label="Decrease indent"
      >
        <IndentDecrease className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={tBtn}
        onClick={() => {
          const ok = editor.isActive("listItem")
            ? editor.chain().focus().sinkListItem("listItem").run()
            : false;
          if (!ok) editor.chain().focus().indentBlock().run();
        }}
        aria-label="Increase indent"
      >
        <IndentIncrease className="h-4 w-4" />
      </Button>
      <span className="mx-1 h-5 w-px bg-border" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs">
            <Plus className="h-3.5 w-3.5" />
            Insert Reference
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem onClick={onInsertCitation}>
            <QuoteIcon className="mr-2 h-4 w-4" /> Cite source…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onInsertFootnote}>
            <Hash className="mr-2 h-4 w-4" /> Footnote (empty)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onInsertEndnote}>
            <BookMarked className="mr-2 h-4 w-4" /> Endnote (empty)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onInsertBibliography}>
            <Library className="mr-2 h-4 w-4" /> Bibliography
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {approvedBrief ? (
        approvedBrief.active ? (
          <span
            className="ml-1 inline-flex items-center gap-1 rounded-md border border-amber-400/60 bg-amber-100/70 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200"
            title="An approved brief is attached — the Writing Agent will use it on the next run."
          >
            <FileText className="h-3 w-3" />
            Brief attached
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-1 h-5 gap-1 px-1 text-[11px] text-amber-900 hover:bg-amber-200/60 dark:text-amber-100 dark:hover:bg-amber-500/25"
              onClick={() => void approvedBrief.onClear()}
              disabled={approvedBrief.clearing}
              aria-label="Clear brief"
            >
              {approvedBrief.clearing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <XCircle className="h-3 w-3" />
              )}
              Clear
            </Button>
          </span>
        ) : (
          <span
            className="ml-1 inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-muted/40 px-2 py-0.5 text-[11px] italic text-muted-foreground"
            title="No approved brief attached — the Writing Agent will run without a brief handoff."
          >
            <FileText className="h-3 w-3" />
            No brief attached
          </span>
        )
      ) : null}
    </div>
  );
};
